function languagePart(localeCode) {
  return String(localeCode || '').split('-')[0].toLowerCase()
}

const PREFERRED_ENGLISH = 'en-US'

function regionPart(localeCode) {
  const parts = String(localeCode || '').split('-')
  return parts.length >= 2 ? parts[1].toUpperCase() : ''
}

function regionPartFromTag(segments) {
  for (let index = segments.length - 1; index >= 1; index -= 1) {
    const part = segments[index]
    if (/^[a-z]{2}$/i.test(part)) {
      return part.toUpperCase()
    }
  }
  return ''
}

function normalizeBrowserTag(tag) {
  const raw = String(tag || '').trim().replace(/_/g, '-')
  if (!raw) return null

  const segments = raw.split('-').filter(Boolean)
  if (!segments.length) return null

  const lang = segments[0].toLowerCase()
  const region = regionPartFromTag(segments)

  return {
    full: region ? `${lang}-${region.toLowerCase()}` : lang,
    lang,
    region
  }
}

/**
 * 读取浏览器语言偏好列表（按 Chrome「首选语言」顺序）。
 * 仅返回原始标签，不做匹配。
 */
export function getBrowserLanguageTags() {
  if (typeof navigator === 'undefined') return []

  const tags = []

  if (Array.isArray(navigator.languages) && navigator.languages.length) {
    for (const tag of navigator.languages) {
      if (tag && !tags.includes(tag)) {
        tags.push(tag)
      }
    }
  } else if (navigator.language) {
    tags.push(navigator.language)
  }

  try {
    const intlLocale = Intl.DateTimeFormat().resolvedOptions().locale
    if (intlLocale && !tags.includes(intlLocale)) {
      tags.push(intlLocale)
    }
  } catch (_) {}

  return tags.filter(Boolean)
}

export function matchBrowserLocale(browserTags, enabledLocales) {
  const locales = (enabledLocales || []).filter((item) => item?.code)
  if (!locales.length) return null

  const codes = locales.map((item) => item.code)
  const normalizedTags = (browserTags || []).map(normalizeBrowserTag).filter(Boolean)
  if (!normalizedTags.length) return null

  // 按浏览器首选顺序逐个尝试：完全匹配 → 同语言匹配
  for (const browser of normalizedTags) {
    const exact = codes.find((code) => code.toLowerCase() === browser.full)
    if (exact) return exact

    const sameLanguage = codes.filter((code) => languagePart(code) === browser.lang)
    if (!sameLanguage.length) continue

    if (browser.region) {
      const sameRegion = sameLanguage.find((code) => regionPart(code) === browser.region)
      if (sameRegion) return sameRegion
    }

    if (sameLanguage.length === 1) return sameLanguage[0]
  }

  // 同语言存在多个变体时，取第一个能匹配到的
  for (const browser of normalizedTags) {
    const sameLanguage = codes.filter((code) => languagePart(code) === browser.lang)
    if (sameLanguage.length) return sameLanguage[0]
  }

  return null
}

function resolveEnglishFallback(locales) {
  const codes = locales.map((item) => item.code)
  const exact = codes.find((code) => code.toLowerCase() === PREFERRED_ENGLISH.toLowerCase())
  if (exact) return exact
  return codes.find((code) => languagePart(code) === 'en') || ''
}

/**
 * 按浏览器首选语言解析站点 locale：
 * 1. 浏览器首选语言命中后台已启用语言 → 使用该语言（如 zh-CN → 中文，ja-JP → 日语）
 * 2. 浏览器语言均未命中（如 pt-BR 而后台未录入） → 英文兜底 en-US
 * 3. 后台未启用任何英文时 → 后台 isDefault / 首项
 */
export function resolveInitialLocale({ saved, enabledLocales, browserTags = getBrowserLanguageTags() }) {
  const locales = (enabledLocales || []).filter((item) => item?.enabled !== false)
  if (!locales.length) return ''

  const isValidSaved = saved && locales.some((item) => item.code === saved)
  if (isValidSaved) return saved

  const browserMatch = matchBrowserLocale(browserTags, locales)
  if (browserMatch) return browserMatch

  const englishFallback = resolveEnglishFallback(locales)
  if (englishFallback) return englishFallback

  const defaultLocale = locales.find((item) => item.isDefault) || locales[0]
  return defaultLocale?.code || ''
}
